/**
 * Het menu dat opengaat als je op een stap klikt.
 *
 * Hier staat alles wat niet in het stapblok zelf hoort: de waarde per onderdeel, de
 * fotoplicht, of de norm aan productie getoond mag worden, de normtijd (alleen admin)
 * en een opmerking bij die stap. Zo blijft het blok klein en past er meer op één
 * scherm — precies wat de stakeholders vroegen.
 *
 * Wie wat mag, komt uit de rechten: de normtijd is voor de admin, de fotoplicht voor
 * productievoorbereiding, de opmerking mag ook de operator zetten. De API toetst
 * hetzelfde nog een keer.
 */
import { useState } from 'react';

import { api, type Weergavestap } from '../api';
import { datumTijd, useGegevens } from '../gegevens';
import { Dialoog, Schakelaar } from './Dialoog';
import { tekstOp } from './Routepaneel';

interface Props {
  bouwnummer: string;
  karId: string;
  stap: Weergavestap;
  onSluiten: () => void;
  /** Na een wijziging opnieuw ophalen; de server is de bron. */
  onGewijzigd: () => void;
}

export function Stapmenu({ bouwnummer, karId, stap, onSluiten, onGewijzigd }: Props) {
  const { mag, meldFout } = useGegevens();
  const [opmerking, setOpmerking] = useState(stap.opmerking ?? '');
  const [opstart, setOpstart] = useState(String(stap.normtijd.opstartMin));
  const [perStuk, setPerStuk] = useState(String(stap.normtijd.perStukMin));
  const [bezig, setBezig] = useState(false);

  const magUren = mag('urenZien');
  const magNorm = mag('normtijdenBewerken');

  const bewaar = async (invoer: Parameters<typeof api.stapInstellen>[3]) => {
    setBezig(true);
    try {
      await api.stapInstellen(bouwnummer, karId, stap.sleutel, invoer);
      onGewijzigd();
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De instelling kon niet opgeslagen worden.');
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialoog titel={`Stap ${stap.kolomnaam}`} onSluiten={onSluiten} breedte={520}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span
          className="hs-mono"
          style={{
            background: stap.kleur,
            color: stap.tekstkleur ?? tekstOp(stap.kleur),
            padding: '4px 9px',
            borderRadius: 'var(--r-blok)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.06em',
          }}
        >
          {stap.kolomnaam}
        </span>
        <span className="hs-muted">{stap.afdeling}</span>
        {stap.status === 'bezig' ? <span className="hs-chip hs-chip--zacht">staat hier nu</span> : null}
      </div>

      {/* De waarde per onderdeel. In het blok staat die van het grootste onderdeel;
          hier staan ze allemaal, zodat een afwijkende maat niet onzichtbaar blijft. */}
      {stap.labelPerOnderdeel.some((p) => p.label) ? (
        <div>
          <span className="hs-sectielabel">Waarde per onderdeel</span>
          <table className="hs-tabel" style={{ marginTop: 4 }}>
            <tbody>
              {stap.labelPerOnderdeel
                .filter((p) => p.label)
                .map((p) => (
                  <tr key={p.onderdeelNummer}>
                    <td className="hs-mono" style={{ width: 40 }}>
                      nr. {p.onderdeelNummer}
                    </td>
                    <td className="hs-mono" style={{ fontWeight: 600 }}>
                      {p.label}
                    </td>
                    <td className="hs-muted">{p.toelichting ?? ''}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div>
        <span className="hs-sectielabel">Instellingen</span>
        <Schakelaar
          label="Foto verplicht bij afstrepen"
          uitleg="Zonder foto weigert de server het afstrepen van deze stap."
          aan={stap.fotoVerplicht}
          uitgeschakeld={!mag('stapInstellen') || bezig}
          onWissel={(aan) => void bewaar({ fotoVerplicht: aan })}
        />
        <Schakelaar
          label="Norm tonen aan productie"
          uitleg="Standaard uit: productie ziet geen uren."
          aan={stap.normZichtbaarVoorProductie}
          uitgeschakeld={!mag('stapInstellen') || bezig}
          onWissel={(aan) => void bewaar({ normZichtbaarVoorProductie: aan })}
        />
      </div>

      {magUren ? (
        <div>
          <span className="hs-sectielabel">
            Normtijd {magNorm ? '(admin)' : '(alleen de admin kan dit wijzigen)'}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 4 }}>
            <label className="hs-veld" style={{ flex: 1 }}>
              <span>Opstarttijd per kar</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <input
                  className="hs-invoer hs-invoer--mono"
                  style={{ width: 70 }}
                  type="number"
                  min={0}
                  step={0.5}
                  value={opstart}
                  disabled={!magNorm || bezig}
                  onChange={(e) => setOpstart(e.target.value)}
                />
                <span className="hs-muted">min</span>
              </span>
            </label>
            <label className="hs-veld" style={{ flex: 1 }}>
              <span>Per stuk</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <input
                  className="hs-invoer hs-invoer--mono"
                  style={{ width: 70 }}
                  type="number"
                  min={0}
                  step={0.1}
                  value={perStuk}
                  disabled={!magNorm || bezig}
                  onChange={(e) => setPerStuk(e.target.value)}
                />
                <span className="hs-muted">min</span>
              </span>
            </label>
            {magNorm ? (
              <button
                type="button"
                className="hs-knop"
                disabled={bezig}
                onClick={() =>
                  void bewaar({
                    normtijd: { opstartMin: Number(opstart), perStukMin: Number(perStuk) },
                  })
                }
              >
                Norm opslaan
              </button>
            ) : null}
          </div>
          {stap.normMinuten != null ? (
            <p className="hs-muted" style={{ marginTop: 5, fontSize: 11 }}>
              Voor deze kar komt dat uit op {String(stap.normMinuten).replace('.', ',')} minuten.
            </p>
          ) : null}
        </div>
      ) : null}

      <label className="hs-veld">
        <span>Opmerking bij deze stap</span>
        <textarea
          className="hs-invoer"
          rows={2}
          value={opmerking}
          disabled={!mag('stapOpmerken') || bezig}
          onChange={(e) => setOpmerking(e.target.value)}
          placeholder="Bijvoorbeeld: zaagblad gewisseld, eerste plaat controleren."
        />
        {mag('stapOpmerken') ? (
          <button
            type="button"
            className="hs-knop"
            style={{ alignSelf: 'flex-start' }}
            disabled={bezig || opmerking === (stap.opmerking ?? '')}
            onClick={() => void bewaar({ opmerking })}
          >
            Opmerking opslaan
          </button>
        ) : null}
      </label>

      {stap.afstrepingen.length > 0 ? (
        <div>
          <span className="hs-sectielabel">Afgestreept door</span>
          <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none' }}>
            {stap.afstrepingen.map((streep, i) => (
              <li key={`${streep.op}-${i}`} className="hs-muted" style={{ fontSize: 11.5 }}>
                {i === 0 ? '' : 'opnieuw · '}
                <b style={{ color: 'var(--tekst)' }}>{streep.door}</b> · {datumTijd(streep.op)}
                {streep.naRework ? ' · na rework' : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {stap.fotos.length > 0 ? (
        <div>
          <span className="hs-sectielabel">Foto's ({stap.fotos.length})</span>
          <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none' }}>
            {stap.fotos.map((foto) => (
              <li key={foto.verwijzing} className="hs-muted" style={{ fontSize: 11.5 }}>
                {foto.bestandsnaam}
                {foto.onderdeelNummer ? ` · onderdeel ${foto.onderdeelNummer}` : ''} · {foto.door} ·{' '}
                {datumTijd(foto.op)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Dialoog>
  );
}
